import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useBackendApi } from '@/hooks/fetch';
import { useOpenPlayerModal } from '@/hooks/playerModal';
import { playerDropCategories } from '@/lib/playerDropCategories';
import type { CrashMapCell, CrashMapDropsResp } from '@shared/vantaCrashMapTypes';
import useSWR from 'swr';

type Props = {
    cell: CrashMapCell | null;
    cellSize: number;
    /** mêmes paramètres que la requête heatmap */
    queryParams: Record<string, string>;
    onClose: () => void;
};

const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
});


/**
 * Liste des drops d'une cellule de la heatmap.
 */
export default function CellDropsDialog({ cell, cellSize, queryParams, onClose }: Props) {
    const openPlayerModal = useOpenPlayerModal();
    const dropsApi = useBackendApi<CrashMapDropsResp>({
        method: 'GET',
        path: '/vanta/crashmap/drops',
    });
    const params = cell ? { ...queryParams, cellXY: `${cell.cx},${cell.cy}` } : null;
    const swr = useSWR(params ? `/vanta/crashmap/drops?${new URLSearchParams(params)}` : null, async () => {
        const data = await dropsApi({ queryParams: params! });
        if (!data) throw new Error('empty_response');
        if ('error' in data) throw new Error(data.error);
        return data;
    }, { revalidateOnFocus: false });

    const openPlayer = (license: string) => {
        onClose();
        openPlayerModal({ license });
    };

    return (
        <Dialog open={!!cell} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Drops in this area</DialogTitle>
                    {cell && (
                        <DialogDescription>
                            {cellSize} m cell around x {cell.x.toFixed(0)}, y {cell.y.toFixed(0)} · {cell.count} drop{cell.count === 1 ? '' : 's'}
                            {cell.hours > 0 ? ` · ${cell.hours} player-hours` : ''}
                        </DialogDescription>
                    )}
                </DialogHeader>
                <div className="overflow-y-auto -mx-2 px-2">
                    {swr.error ? (
                        <p className="text-sm text-destructive">{(swr.error as Error).message}</p>
                    ) : !swr.data ? (
                        <p className="text-sm text-muted-foreground">Loading…</p>
                    ) : !swr.data.drops.length ? (
                        <p className="text-sm text-muted-foreground">No drop found.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground text-left">
                                <tr className="border-b">
                                    <th className="py-1.5 pr-2 font-medium">Time</th>
                                    <th className="py-1.5 pr-2 font-medium">Player</th>
                                    <th className="py-1.5 pr-2 font-medium">Category</th>
                                    <th className="py-1.5 font-medium">Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {swr.data.drops.map((d, i) => {
                                    const cat = playerDropCategories[d.category];
                                    return (
                                        <tr key={`${d.ts}-${i}`} className="border-b last:border-0 align-top">
                                            <td className="py-1.5 pr-2 whitespace-nowrap font-mono text-xs">
                                                {dateFormatter.format(new Date(d.ts))}
                                            </td>
                                            <td className="py-1.5 pr-2 max-w-40 truncate">
                                                {d.license ? (
                                                    <button type="button" className="hover:underline text-left" onClick={() => openPlayer(d.license!)}>
                                                        {d.name}
                                                    </button>
                                                ) : d.name}
                                            </td>
                                            <td className="py-1.5 pr-2 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="size-2 rounded-full" style={{ backgroundColor: cat?.color }} />
                                                    {cat?.label ?? d.category}
                                                </span>
                                                {d.mass && <span className="ml-1.5 text-xs text-warning">mass</span>}
                                            </td>
                                            <td className="py-1.5 break-all text-muted-foreground">
                                                {d.resource ? `[${d.resource}] ` : ''}{d.reason}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    {swr.data && swr.data.total > swr.data.drops.length && (
                        <p className="mt-2 text-xs text-muted-foreground">
                            Showing the {swr.data.drops.length} most recent of {swr.data.total} drops.
                        </p>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
